/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
/**
 * Where auth-svc lives, as seen from the browser (AI-LAUNCH-40).
 *
 * Three separate call sites used to compute this by splitting the current
 * hostname and replacing the first label with "auth". That is wrong in two ways:
 *
 *  1. White-label custom domains are a real feature (msp_branding.custom_domain).
 *     On `portal.acmemsp.com` the trick yields `https://auth.acmemsp.com` — an
 *     origin SKYXOPS does not control — and the accept-invite page appended
 *     `&invite=<token>` to that redirect, handing a one-time credential to
 *     whoever owns that hostname.
 *  2. It breaks on any single-label host: `'localhost:8000'.split('.')` is a
 *     one-element array, so the result was the literal `http://auth`.
 *
 * The origin is therefore configuration, not a string transformation of whatever
 * host the page happens to be served from. Resolution order:
 *
 *   1. `VITE_AUTH_SVC_ORIGIN` baked in at build time — authoritative, and the
 *      only mechanism that is correct on a custom domain.
 *   2. A host under `.skyxops.io` — safe to derive, because we own it.
 *   3. localhost — the documented dev port for auth-svc.
 *   4. Anything else — empty string. Callers must treat that as "SSO is not
 *      configured here" and refuse, rather than redirect somewhere unowned.
 *      Failing closed is the whole point: an unknown origin is exactly the case
 *      that used to leak the invite token.
 */

declare const __AUTH_SVC_ORIGIN__: string;

const SKYXOPS_SUFFIX = '.skyxops.io';
const DEV_AUTH_ORIGIN = 'http://localhost:8001';

export function authOrigin(): string {
  const configured = typeof __AUTH_SVC_ORIGIN__ === 'undefined' ? '' : __AUTH_SVC_ORIGIN__;
  if (configured) return configured.replace(/\/+$/, '');

  const { protocol, host, hostname } = window.location;

  if (hostname === 'localhost' || hostname === '127.0.0.1') return DEV_AUTH_ORIGIN;

  if (hostname.endsWith(SKYXOPS_SUFFIX)) {
    const labels = host.split('.');
    labels[0] = 'auth';
    return `${protocol}//${labels.join('.')}`;
  }

  return '';
}

/** True when an auth-svc origin could be resolved for this deployment. */
export function hasAuthOrigin(): boolean {
  return authOrigin() !== '';
}
